import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  BsChevronDown,
  BsChevronLeft,
  BsChevronRight,
  BsEnvelope,
  BsPaperclip,
  BsSearch,
  BsSend,
  BsPlusCircleFill,
} from "react-icons/bs";
import Loader from "../../components/Dailogs/Loader";
import { useGmail } from "../../components/Google/useGmailHook";
import "react-quill/dist/quill.snow.css";
import "../../assets/styles/local/ContactEmails.css";
import { fetchScreenTranslations } from "../../utils/helpers/translations";
import {
  downloadGoogleAttachment,
  sendGoogleEmail,
  uploadFilesToS3,
  getGoogleEmailId,
} from "../../redux/constants/integrationApi";
import { ContactEmailModal } from "./ContactEmailModal";
import { ContactSendEmailModal } from "./ContactSendEmailModal";
import { FcGoogle } from "react-icons/fc";
import { SiMicrosoftoutlook } from "react-icons/si";

const PAGE_SIZE = 10;
const MAX_BASE64_ATTACHMENT_SIZE_MB = 5;
const MAX_BASE64_ATTACHMENT_SIZE_BYTES =
  MAX_BASE64_ATTACHMENT_SIZE_MB * 1024 * 1024;

export const ContactEmails = ({ fromEmail, emailSource, setEmailSource }) => {
  const [FetchTranslationList, setFetchTranslationList] = useState({});
  const [composeQuotedBody, setComposeQuotedBody] = useState("");
  const t = useCallback(
    (key) => FetchTranslationList?.[key] || key,
    [FetchTranslationList],
  );
  const [totalPages, setTotalPages] = useState(1);
  const [totalEmails, setTotalEmails] = useState(0);

  const extractEmailAddress = (value) => {
    const match = String(value || "").match(/<([^>]+)>/);
    return (match?.[1] || String(value || "").trim()).replace(
      /^['"]|['"]$/g,
      "",
    );
  };

  const buildReplyBody = (email) => {
    if (!email) return "";
    const sender = extractEmailAddress(email.from || "");
    const date = [email?.date, email?.time].filter(Boolean).join(" ");
    const quotedBody = String(email.body || "").trim();

    return `
    <p><br></p>
    <p>On ${date}, ${sender} wrote:</p>
    <blockquote style="margin:0;padding-left:12px;border-left:2px solid #ccc;">
      ${quotedBody}
    </blockquote>
  `;
  };

  const buildForwardBody = (email) => {
    if (!email) return "";
    const from = extractEmailAddress(email.from || "");
    const to = extractEmailAddress(email.to || "");
    const subject = email.subject || "";
    const date = [email?.date, email?.time].filter(Boolean).join(" ");
    const body = String(email.body || "").trim();

    return `
    <p><br></p>
    <p><strong>---------- Forwarded message ---------</strong></p>
    <p><strong>From:</strong> ${from}</p>
    <p><strong>To:</strong> ${to}</p>
    <p><strong>Subject:</strong> ${subject}</p>
    <p><strong>Date:</strong> ${date}</p>
    <div>${body}</div>
  `;
  };

  const { loading, fetchEmails } = useGmail();
  const [emails, setEmails] = useState([]);
  const [error, setError] = useState("");
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [emailPopupShow, setEmailPopupShow] = useState(false);
  const [downloadingAttachmentIndex, setDownloadingAttachmentIndex] =
    useState(null);
  const [composeShow, setComposeShow] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [composeError, setComposeError] = useState("");
  const [composeAttachments, setComposeAttachments] = useState([]);
  const [sourceDropdownOpen, setSourceDropdownOpen] = useState(false);
  const [composeForm, setComposeForm] = useState({
    from: "",
    to: "",
    cc: "",
    bcc: "",
    subject: "",
    body: "",
  });
  const [composeMode, setComposeMode] = useState("new");
  const [composeContextEmail, setComposeContextEmail] = useState(null);

  useEffect(() => {
    fetchScreenTranslations("Add-Contact").then((data) =>
      setFetchTranslationList(data),
    );
  }, []);

  useEffect(() => {
    let ignore = false;

    const loadEmails = async () => {
      setError("");
      setEmails([]);
      // setSearchText("");
      // setCurrentPage(1);

      if (!fromEmail) {
        return;
      }

      const response = await fetchEmails({
        filterEmail: fromEmail,
        pageToken: "",
        pagerequire: PAGE_SIZE,
        pagenumber: currentPage,
      });

      if (ignore) {
        return;
      }

      if (!response.error) {
        setEmails(response.records);
        setTotalPages(response.totalPages || 1);
        setTotalEmails(response.totalEmails || 0);
      }

      if (response.signinRequire) {
        setError(t("Google authentication required."));
        return;
      }

      if (response.error) {
        setError(response.error);
        return;
      }

      setEmails(response.records);
      setTotalPages(response.totalPages || 1);
      setTotalEmails(response.totalEmails || 0);
    };

    loadEmails();

    return () => {
      ignore = true;
    };
  }, [fetchEmails, fromEmail, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, fromEmail]);


  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (currentPage - 1) * PAGE_SIZE + 1; // 1-based
  const endIndex = Math.min(currentPage * PAGE_SIZE, totalEmails);
  const pagedEmails = emails;
  const rangeText =
    totalEmails === 0
      ? "0-0 of 0"
      : `${startIndex}-${endIndex} of ${totalEmails}`;

  const openEmail = (email) => {
    setSelectedEmail(email);
    setEmailPopupShow(true);
  };

  const closeEmail = () => {
    setEmailPopupShow(false);
    setSelectedEmail(null);
    setDownloadingAttachmentIndex(null);
  };

  const handleDownloadAttachment = async (event, file, index) => {
    event.stopPropagation();

    if (!file?.attachmentUrl || downloadingAttachmentIndex !== null) {
      return;
    }

    try {
      setDownloadingAttachmentIndex(index);

      const blob = await downloadGoogleAttachment(file.attachmentUrl);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = file.fileName || "attachment";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.setTimeout(() => window.URL.revokeObjectURL(url), 0);
    } catch (error) {
      console.error("Google attachment download failed:", error);
    } finally {
      setDownloadingAttachmentIndex(null);
    }
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const splitRecipients = (value) =>
    (value || "")
      .split(/[;,\s]+/)
      .map((item) => item.trim())
      .filter(Boolean);

  const openCompose = async (mode = "new", email = null) => {
    setComposeForm({
      from: "",
      to: "",
      cc: "",
      bcc: "",
      subject: "",
      body: "",
    });
    setComposeQuotedBody("");
    setSendingEmail(false);
    setComposeError("");
    setComposeAttachments([]);
    setComposeMode(mode);
    setComposeContextEmail(email);

    const senderEmail = extractEmailAddress(email?.from || "");
    const subject = email?.subject || "";

    let fromField = window.localStorage.getItem("googleEmail") || "";

    if (!fromField) {
      try {
        const response = await getGoogleEmailId();

        if (response?.data?.emailId) {
          fromField = response.data.emailId;
          window.localStorage.setItem("googleEmail", fromField);
        }
      } catch (error) {
        console.error("Failed to fetch Google email ID:", error);
      }
    }

    const nextForm = {
      from: fromField,
      to:
        mode === "reply"
          ? senderEmail
          : mode === "forward"
            ? ""
            : fromEmail || "",
      cc: "",
      bcc: "",
      subject:
        mode === "reply"
          ? subject?.toLowerCase().startsWith("re:")
            ? subject
            : `Re: ${subject}`
          : mode === "forward"
            ? subject?.toLowerCase().startsWith("fwd:")
              ? subject
              : `Fwd: ${subject}`
            : "",
      body: "",
    };

    setComposeQuotedBody(
      mode === "reply"
        ? buildReplyBody(email)
        : mode === "forward"
          ? buildForwardBody(email)
          : "",
    );

    setComposeForm(nextForm);
    setComposeShow(true);
  };

  const closeCompose = () => {
    if (sendingEmail) return;
    setComposeShow(false);
    setComposeError("");
    setComposeAttachments([]);
    setComposeMode("new");
    setComposeContextEmail(null);
    setComposeQuotedBody("");
  };

  const reloadEmails = async () => {
    if (!fromEmail) return;

    const response = await fetchEmails({
      filterEmail: fromEmail,
      pageToken: "",
      pagerequire: PAGE_SIZE,
      pagenumber: 1,
    });

    if (!response.signinRequire && !response.error) {
      setCurrentPage(1);
      setEmails(response.records);
      setTotalPages(response.totalPages || 1);
      setTotalEmails(response.totalEmails || 0);
    }
  };

  const isBodyEmpty = (html) => !html?.replace(/<(.|\n)*?>/g, "").trim();
  const handleSendEmail = async () => {
    const to = splitRecipients(composeForm.to);
    const cc = splitRecipients(composeForm.cc);
    const bcc = splitRecipients(composeForm.bcc);

    const invalidRecipients = [...to, ...cc, ...bcc].filter(
      (email) => !isValidEmail(email),
    );

    if (invalidRecipients.length) {
      setComposeError(
        `${t("Invalid email address")}: ${invalidRecipients.join(", ")}`,
      );
      return;
    }

    if (!to.length) {
      setComposeError(t("Please fill To field."));
      return;
    }

    if (!composeForm.subject.trim()) {
      setComposeError(t("Please fill Subject field."));
      return;
    }

    if (isBodyEmpty(composeForm.body)) {
      setComposeError(t("Please fill Body field."));
      return;
    }

    if (isBodyEmpty(composeForm.body) && !composeQuotedBody) {
      setComposeError(t("Please fill Body field."));
      return;
    }

    try {
      setSendingEmail(true);
      setComposeError("");
      const smallFiles = composeAttachments.filter(
        (file) => file.size <= MAX_BASE64_ATTACHMENT_SIZE_BYTES,
      );

      const largeFiles = composeAttachments.filter(
        (file) => file.size > MAX_BASE64_ATTACHMENT_SIZE_BYTES,
      );

      const attachments = await Promise.all(
        smallFiles.map(async (file) => ({
          fileName: file.name,
          mimeType: file.type || "application/octet-stream",
          contentBytes: await fileToBase64(file),
        })),
      );

      const uploadedUrls =
        largeFiles.length > 0 ? await uploadFilesToS3(largeFiles) : [];

      const normalizedBody = composeForm.body.replace(
        /<p([^>]*)>/g,
        '<p$1 style="margin:0;padding:0;">',
      );

      const bodyWithAttachmentLinks = `${normalizedBody}${buildAttachmentLinksHtml(
        largeFiles,
        uploadedUrls,
      )}${composeQuotedBody}`;

      await sendGoogleEmail({
        from: composeForm.from,
        to,
        cc,
        bcc,
        subject: composeForm.subject.trim(),
        body: bodyWithAttachmentLinks,
        bodyContentType: "HTML",
        attachments,
      });

      setSendingEmail(false);
      setComposeShow(false);
      setComposeForm({
        from: "",
        to: "",
        cc: "",
        bcc: "",
        subject: "",
        body: "",
      });

      setComposeAttachments([]);
      closeEmail();
      reloadEmails();
    } catch (error) {
      setComposeError(
        error?.response?.data?.message ||
          error?.message ||
          t("Unable to send email."),
      );
    } finally {
      setSendingEmail(false);
    }
  };

  const composeEditorModules = {
    toolbar: {
      container: "#contact-email-compose-toolbar",
    },
  };

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        const result = String(reader.result || "");
        resolve(result.split(",")[1] || "");
      };

      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const buildAttachmentLinksHtml = (files, urls) => {
    if (!files.length || !urls.length) {
      return "";
    }

    const links = files
      .map((file, index) => {
        const url = urls[index];

        if (!url) {
          return "";
        }

        return `<li><a href="${url}" target="_blank" rel="noreferrer">${file.name}</a></li>`;
      })
      .filter(Boolean)
      .join("");

    if (!links) {
      return "";
    }

    return `
    <p><strong>${t("Attachments")}:</strong></p>
    <ul>${links}</ul>
  `;
  };

  const handleComposeAttachmentChange = (event) => {
    const files = Array.from(event.target.files || []);

    setComposeAttachments((currentFiles) => [...currentFiles, ...files]);

    event.target.value = "";
  };

  const removeComposeAttachment = (indexToRemove) => {
    setComposeAttachments((files) =>
      files.filter((_, index) => index !== indexToRemove),
    );
  };

  const handleEmailSourceSelect = (source) => {
    setEmailSource(source);
    setSourceDropdownOpen(false);
  };

  const hasNoSearchResults =
    Boolean(searchText.trim()) && !loading;
  const hasNoEmails =
    !searchText.trim() && emails.length === 0 && !loading && !error;

  return (
    <>
      <div className="contact-emails-wrapper">
        <div className="contact-emails-search-box">
          <BsSearch className="contact-emails-search-icon" />
          <input
            type="text"
            value={searchText}
            placeholder={t("Search")}
            onChange={(event) => setSearchText(event.target.value)}
            className="contact-emails-search-input"
          />
          <div
            className="contact-emails-source-dropdown"
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget)) {
                setSourceDropdownOpen(false);
              }
            }}
          >
            <button
              type="button"
              className="contact-emails-source-trigger"
              aria-expanded={sourceDropdownOpen}
              onClick={() => setSourceDropdownOpen((isOpen) => !isOpen)}
            >
              {emailSource === "gmail" ? <FcGoogle /> : <SiMicrosoftoutlook />}
              <span>{emailSource === "gmail" ? t("Gmail") : t("Outlook")}</span>
              <BsChevronDown
                className={`contact-emails-source-chevron${
                  sourceDropdownOpen ? " is-open" : ""
                }`}
              />
            </button>

            <div
              className={`contact-emails-source-menu${
                sourceDropdownOpen ? " is-open" : ""
              }`}
            >
              <button
                type="button"
                onClick={() => handleEmailSourceSelect("gmail")}
              >
                <FcGoogle />
                <span>{t("Gmail")}</span>
              </button>

              <button
                type="button"
                onClick={() => handleEmailSourceSelect("outlook")}
              >
                <SiMicrosoftoutlook />
                <span>{t("Outlook")}</span>
              </button>
            </div>
          </div>
        </div>

        <div className="contact-emails-table-header">
          <button
            type="button"
            className="contact-emails-title contact-emails-title-button"
            onClick={openCompose}
            title={t("Send Email")}
          >
            <span className="contact-emails-title-icon-wrap">
              <BsEnvelope size={20} />
              <BsPlusCircleFill className="contact-emails-title-badge" />
            </span>
            <span>{t("Email")}</span>
          </button>

          <div className="contact-emails-pagination">
            <span>{rangeText}</span>
            <button
              type="button"
              disabled={safeCurrentPage <= 1 || loading}
              onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
              className="contact-emails-page-button"
              aria-label="Previous page"
            >
              <BsChevronLeft />
            </button>

            <span className="contact-emails-page-number">
              {currentPage}
            </span>

            <button
              type="button"
              disabled={safeCurrentPage >= totalPages || loading}
              onClick={() =>
                setCurrentPage((page) => Math.min(totalPages, page + 1))
              }
              className="contact-emails-page-button"
              aria-label="Next page"
            >
              <BsChevronRight />
            </button>
          </div>
        </div>

        <div className="contact-emails-grid-header">
          <div />
          <div>{t("Subject")}</div>
          <div>{t("Date")}</div>
          <div />
        </div>

        <div className="contact-emails-rows">
          {pagedEmails.map((email, index) => (
            <button
              type="button"
              key={email.id || `${email.threadId}-${startIndex + index}`}
              onClick={() => openEmail(email)}
              className="contact-emails-row"
            >
              <span className="contact-emails-icon-cell">
                {email.direction === "sent" ? <BsSend /> : <BsEnvelope />}
              </span>
              <span className="contact-emails-subject">{email.subject}</span>
              <span className="contact-emails-date-cell">{email.date}</span>
              <span className="contact-emails-time-cell">{email.time}</span>
              <span className="contact-emails-attachment-cell">
                {email.attachments.length > 0 ? <BsPaperclip /> : ""}
              </span>
            </button>
          ))}
        </div>

        {loading && (
          <div className="contact-emails-message">
            <Loader />
          </div>
        )}

        {Boolean(error) && <div className="contact-emails-error">{error}</div>}

        {hasNoSearchResults && (
          <div className="contact-emails-error">{t("No emails found")}</div>
        )}

        {hasNoEmails && (
          <div className="contact-emails-error">
            {t("No Emails Available From")}{" "}
            {fromEmail ? fromEmail : t("No email registered")}
          </div>
        )}
      </div>

      <ContactEmailModal
        show={emailPopupShow}
        onHide={closeEmail}
        selectedEmail={selectedEmail}
        downloadingAttachmentIndex={downloadingAttachmentIndex}
        onDownloadAttachment={handleDownloadAttachment}
        onReplyEmail={(email) => openCompose("reply", email)}
        onForwardEmail={(email) => openCompose("forward", email)}
        t={t}
      />

      <ContactSendEmailModal
        show={composeShow}
        onHide={closeCompose}
        composeForm={composeForm}
        setComposeForm={setComposeForm}
        composeAttachments={composeAttachments}
        sendingEmail={sendingEmail}
        composeError={composeError}
        onSendEmail={handleSendEmail}
        onAttachmentChange={handleComposeAttachmentChange}
        onRemoveAttachment={removeComposeAttachment}
        composeEditorModules={composeEditorModules}
        composeMode={composeMode}
        composeQuotedBody={composeQuotedBody}
        t={t}
      />
    </>
  );
};
